import React, { useState, useEffect } from 'react';

function Header({ currentView, setView }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  // Close mobile menu on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const viewPaths = {
    home: '/',
    services: '/services/',
    'bpo-services': '/bpo-services/',
    'logo-design': '/logo-design/',
    'ecommerce-web-development': '/ecommerce-web-development/',
    'it-services': '/it-services/',
    'web-design': '/web-design/',
    'digital-marketing': '/digital-marketing/',
    'seo-services': '/seo-services/',
    'social-media-marketing': '/social-media-marketing/',
    'web-development': '/web-development/',
    team: '/team/',
    careers: '/careers/',
    about: '/about/',
    contact: '/contact/'
  };

  const handleNavClick = (e, view, targetAnchor = null) => {
    e.preventDefault();
    setMobileMenuOpen(false);

    const path = viewPaths[view] || '/';
    window.history.pushState({}, '', path);
    setView(view);

    if (targetAnchor) {
      setTimeout(() => {
        const element = document.querySelector(targetAnchor);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 150);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    window.history.pushState({}, '', '/');
    setView('home');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header className="header">
      <div className="container header-container">
        <div className="logo-container" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
          <img
            src="/assets/images/Experts-Logo-White.png"
            alt="Experts Communication Logo"
            className="logo"
          />
        </div>

        {/* Desktop Navigation */}
        <nav className="desktop-nav">
          <ul className="nav-menu">
            <li className="nav-item">
              <a href="/" onClick={(e) => handleNavClick(e, 'home')} className={`nav-link ${currentView === 'home' ? 'active' : ''}`}>Home</a>
            </li>
            <li className="nav-item services-dropdown">
              <a href="/services/" onClick={(e) => handleNavClick(e, 'services')} className={`nav-link ${currentView === 'services' ? 'active' : ''}`}>
                Services
                <span className="dropdown-arrow">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </a>

              {/* Megamenu Dropdown */}
              <div className="megamenu">
                <div className="container megamenu-container">
                  {/* Column 1 - Built to Win */}
                  <div className="megamenu-col brand-col">
                    <h3>Built to Win</h3>
                    <p>
                      Transforming business with our future-ready tech solutions.
                      Get custom products for accelerated digital transformation across
                      industries globally.
                    </p>
                    <img
                      src="/assets/images/iphone-15-pro-big-1.webp"
                      alt="Built to win showcase"
                      className="megamenu-img"
                    />
                  </div>

                  {/* Column 2 */}
                  <div className="megamenu-col">
                    <div className="megamenu-item" onClick={(e) => handleNavClick(e, 'bpo-services')} style={{ cursor: 'pointer' }}>
                      <h4>BPO Services</h4>
                      <p>Empowering startups and enterprises with custom digital solutions for transformation.</p>
                    </div>
                    <div className="megamenu-item" onClick={(e) => handleNavClick(e, 'logo-design')} style={{ cursor: 'pointer' }}>
                      <h4>Logo Design</h4>
                      <p>Get the latest news and updates about Technomite's impact.</p>
                    </div>
                    <div className="megamenu-item" onClick={(e) => handleNavClick(e, 'ecommerce-web-development')} style={{ cursor: 'pointer' }}>
                      <h4>E Commerce Web Development</h4>
                      <p>Refer talent to Experts Communication's and earn rewards.</p>
                    </div>
                  </div>

                  {/* Column 3 */}
                  <div className="megamenu-col">
                    <div className="megamenu-item" onClick={(e) => handleNavClick(e, 'it-services')} style={{ cursor: 'pointer' }}>
                      <h4>IT Services</h4>
                      <p>Scalable cloud infrastructure, data management, cybersecurity & tech support.</p>
                    </div>
                    <div className="megamenu-item" onClick={(e) => handleNavClick(e, 'web-design')} style={{ cursor: 'pointer' }}>
                      <h4>Web Design</h4>
                      <p>Attractive website design crafted to tell your brand story and convert visitors.</p>
                    </div>
                  </div>

                  {/* Column 4 */}
                  <div className="megamenu-col">
                    <div className="megamenu-item">
                      <h4 onClick={(e) => handleNavClick(e, 'digital-marketing')} style={{ cursor: 'pointer' }}>
                        Digital Marketing
                      </h4>
                      <ul className="megamenu-sublist">
                        <li>
                          <a href="/seo-services/" onClick={(e) => handleNavClick(e, 'seo-services')}>
                            <span className="sublist-arrow">&#8250;</span> SEO Services
                          </a>
                        </li>
                        <li>
                          <a href="/social-media-marketing/" onClick={(e) => handleNavClick(e, 'social-media-marketing')}>
                            <span className="sublist-arrow">&#8250;</span> Social Media Marketing
                          </a>
                        </li>
                      </ul>
                    </div>
                    <div className="megamenu-item" onClick={(e) => handleNavClick(e, 'web-development')} style={{ cursor: 'pointer' }}>
                      <h4>Web Development</h4>
                      <p>Engaging, high-performing websites built for functionality and growth.</p>
                    </div>
                  </div>
                </div>
              </div>
            </li>
            <li className="nav-item">
              <a href="/team/" onClick={(e) => handleNavClick(e, 'team')} className={`nav-link ${currentView === 'team' ? 'active' : ''}`}>Team</a>
            </li>
            <li className="nav-item">
              <a href="/careers/" onClick={(e) => handleNavClick(e, 'careers')} className={`nav-link ${currentView === 'careers' ? 'active' : ''}`}>Careers</a>
            </li>
            <li className="nav-item">
              <a href="/about/" onClick={(e) => handleNavClick(e, 'about')} className={`nav-link ${currentView === 'about' ? 'active' : ''}`}>About Us</a>
            </li>
            <li className="nav-item">
              <a href="/contact/" onClick={(e) => handleNavClick(e, 'contact')} className={`nav-link ${currentView === 'contact' ? 'active' : ''}`}>Contact Us</a>
            </li>
            <li className="nav-item">
              <button className="btn-quote" onClick={(e) => handleNavClick(e, 'contact')}>GET A QUOTE</button>
            </li>
          </ul>
        </nav>

        {/* Mobile Hamburger Toggle Button (mobile screen only) */}
        <button
          type="button"
          className={`mobile-menu-toggle ${mobileMenuOpen ? 'open' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={mobileMenuOpen}
        >
          <span className="hamburger-line line-1"></span>
          <span className="hamburger-line line-2"></span>
          <span className="hamburger-line line-3"></span>
        </button>
      </div>

      {/* Mobile Drawer Backdrop */}
      <div
        className={`mobile-menu-backdrop ${mobileMenuOpen ? 'active' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile Navigation Drawer */}
      <div className={`mobile-nav-drawer ${mobileMenuOpen ? 'open' : ''}`} aria-hidden={!mobileMenuOpen}>
        <div className="mobile-nav-header">
          <div
            className="mobile-logo-container"
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
          >
            <img
              src="/assets/images/Experts-Logo-White.png"
              alt="Experts Communication Logo"
              className="mobile-nav-logo"
            />
          </div>
          <button
            type="button"
            className="mobile-nav-close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close navigation menu"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="mobile-nav-body">
          <ul className="mobile-nav-menu">
            <li className="mobile-nav-item">
              <a
                href="/"
                onClick={(e) => handleNavClick(e, 'home')}
                className={`mobile-nav-link ${currentView === 'home' ? 'active' : ''}`}
              >
                Home
              </a>
            </li>

            {/* Mobile Services Accordion */}
            <li className={`mobile-nav-item mobile-services-item ${servicesOpen ? 'expanded' : ''}`}>
              <div className="mobile-services-header">
                <a
                  href="/services/"
                  onClick={(e) => handleNavClick(e, 'services')}
                  className={`mobile-nav-link ${currentView === 'services' ? 'active' : ''}`}
                >
                  Services
                </a>
                <button
                  type="button"
                  className="mobile-submenu-toggle"
                  onClick={() => setServicesOpen(!servicesOpen)}
                  aria-label="Toggle Services submenu"
                  aria-expanded={servicesOpen}
                >
                  <svg
                    className={`mobile-chevron ${servicesOpen ? 'rotate' : ''}`}
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              </div>

              {/* Submenu */}
              <div className={`mobile-submenu ${servicesOpen ? 'open' : ''}`}>
                <ul className="mobile-submenu-list">
                  <li>
                    <a
                      href="/bpo-services/"
                      onClick={(e) => handleNavClick(e, 'bpo-services')}
                      className={`mobile-sub-link ${currentView === 'bpo-services' ? 'active' : ''}`}
                    >
                      BPO Services
                    </a>
                  </li>
                  <li>
                    <a
                      href="/it-services/"
                      onClick={(e) => handleNavClick(e, 'it-services')}
                      className={`mobile-sub-link ${currentView === 'it-services' ? 'active' : ''}`}
                    >
                      IT Services
                    </a>
                  </li>
                  <li>
                    <a
                      href="/web-design/"
                      onClick={(e) => handleNavClick(e, 'web-design')}
                      className={`mobile-sub-link ${currentView === 'web-design' ? 'active' : ''}`}
                    >
                      Web Design
                    </a>
                  </li>
                  <li>
                    <a
                      href="/web-development/"
                      onClick={(e) => handleNavClick(e, 'web-development')}
                      className={`mobile-sub-link ${currentView === 'web-development' ? 'active' : ''}`}
                    >
                      Web Development
                    </a>
                  </li>
                  <li>
                    <a
                      href="/ecommerce-web-development/"
                      onClick={(e) => handleNavClick(e, 'ecommerce-web-development')}
                      className={`mobile-sub-link ${currentView === 'ecommerce-web-development' ? 'active' : ''}`}
                    >
                      E Commerce Web Development
                    </a>
                  </li>
                  <li>
                    <a
                      href="/logo-design/"
                      onClick={(e) => handleNavClick(e, 'logo-design')}
                      className={`mobile-sub-link ${currentView === 'logo-design' ? 'active' : ''}`}
                    >
                      Logo Design
                    </a>
                  </li>
                  <li>
                    <a
                      href="/digital-marketing/"
                      onClick={(e) => handleNavClick(e, 'digital-marketing')}
                      className={`mobile-sub-link ${currentView === 'digital-marketing' ? 'active' : ''}`}
                    >
                      Digital Marketing
                    </a>
                  </li>
                  <li className="mobile-sub-nested">
                    <a
                      href="/seo-services/"
                      onClick={(e) => handleNavClick(e, 'seo-services')}
                      className={`mobile-sub-link nested ${currentView === 'seo-services' ? 'active' : ''}`}
                    >
                      <span className="sublist-arrow">&#8250;</span> SEO Services
                    </a>
                  </li>
                  <li className="mobile-sub-nested">
                    <a
                      href="/social-media-marketing/"
                      onClick={(e) => handleNavClick(e, 'social-media-marketing')}
                      className={`mobile-sub-link nested ${currentView === 'social-media-marketing' ? 'active' : ''}`}
                    >
                      <span className="sublist-arrow">&#8250;</span> Social Media Marketing
                    </a>
                  </li>
                </ul>
              </div>
            </li>

            <li className="mobile-nav-item">
              <a
                href="/team/"
                onClick={(e) => handleNavClick(e, 'team')}
                className={`mobile-nav-link ${currentView === 'team' ? 'active' : ''}`}
              >
                Team
              </a>
            </li>
            <li className="mobile-nav-item">
              <a
                href="/careers/"
                onClick={(e) => handleNavClick(e, 'careers')}
                className={`mobile-nav-link ${currentView === 'careers' ? 'active' : ''}`}
              >
                Careers
              </a>
            </li>
            <li className="mobile-nav-item">
              <a
                href="/about/"
                onClick={(e) => handleNavClick(e, 'about')}
                className={`mobile-nav-link ${currentView === 'about' ? 'active' : ''}`}
              >
                About Us
              </a>
            </li>
            <li className="mobile-nav-item">
              <a
                href="/contact/"
                onClick={(e) => handleNavClick(e, 'contact')}
                className={`mobile-nav-link ${currentView === 'contact' ? 'active' : ''}`}
              >
                Contact Us
              </a>
            </li>
          </ul>

          <div className="mobile-nav-footer">
            <button
              className="btn-quote mobile-btn-quote"
              onClick={(e) => handleNavClick(e, 'contact')}
            >
              GET A QUOTE
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
